import React, { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    Timestamp,
    where
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import "../../App.css";
import moment from 'moment';
import {
    Box,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button
} from "@mui/material";

// --- CUSTOM COLOR PALETTE & THEME ---
const COLORS = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505',
    bgDark: '#031c17',
    borderGreen: 'rgba(21, 83, 50, 0.6)'
};

const TEXT_SECONDARY = "rgba(222, 226, 177, 0.7)";

// Helper to chunk arrays into slices of 30 for Firestore 'in' queries
const chunkArray = (arr, size = 30) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
    }
    return chunks;
};

function Selfreport() {
    const printRef = useRef();
    const [userData, setUserData] = useState(null);
    const [username, setusername] = useState("");
    const [bookingData, setbookingData] = useState([]);
    const [payhistory, setpayhistory] = useState([]);
    const [paymodes, setpaymodes] = useState([]);
    const [propData, setpropData] = useState({});

    // Summary Totals
    const [allllroomamount, setallllroomamount] = useState(0);
    const [allllserviuceamount, setallllserviuceamount] = useState(0);
    const [allllfoodamount, setallllfoodamount] = useState(0);
    const [alllltotalamount, setalllltotalamount] = useState(0);
    const [roomscount, setroomscount] = useState(0);
    const [alladults, setalladults] = useState(0);
    const [allchilds, setallchilds] = useState(0);

    // Track user session and property info
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                setUserData(user);
                try {
                    const roleSnap = await getDoc(doc(db, "authroles", user.uid));
                    if (roleSnap.exists()) {
                        const roleData = roleSnap.data();
                        setusername(roleData.name || user.displayName || "Staff");

                        if (roleData.propertyId) {
                            const propSnap = await getDoc(doc(db, "properties", roleData.propertyId));
                            if (propSnap.exists()) {
                                setpropData({ id: propSnap.id, ...propSnap.data() });
                            }
                        }
                    }
                    await fetchUsers(user.uid);
                } catch (error) {
                    console.error("Auth role error: ", error);
                }
            }
        });
        return () => unsubscribe();
    }, []);

    const fetchUsers = async (activeUid) => {
        try {
            const uidToUse = activeUid || userData?.uid;
            if (!uidToUse) return;

            // 1. Setup Dates & Timestamps
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);

            const todayTimestamp = Timestamp.fromDate(today);
            const tomorrowTimestamp = Timestamp.fromDate(tomorrow);
            const reportDate = moment().format('YYYY-MM-DD');

            // 2. Fetch Payments & Services
            const [payHistorySnapshot, servicesSnapshot] = await Promise.all([
                getDocs(
                    query(
                        collection(db, "payhistory"),
                        where("uid", "==", uidToUse),
                        where("createdAt", ">=", todayTimestamp),
                        where("createdAt", "<", tomorrowTimestamp),
                        where("paymenttype", "==", "B"),
                        where("status", "==", "Working")
                    )
                ),
                getDocs(
                    query(
                        collection(db, "bookingservices"),
                        where("uid", "==", uidToUse),
                        where("type", "==", "Booking"),
                        where("createdAt", ">=", todayTimestamp),
                        where("createdAt", "<", tomorrowTimestamp)
                    )
                )
            ]);

            // 3. Fetch Payment Modes & Reservations Lookup
            const payHistoryDocs = payHistorySnapshot.docs;
            const bookingIdsToFetch = Array.from(
                new Set(payHistoryDocs.map(d => d.data().bookingid).filter(Boolean))
            );

            const allPaymentModesSnapshot = await getDocs(collection(db, "paymentmodes"));
            const paymentModeLookup = allPaymentModesSnapshot.docs.reduce((acc, d) => {
                acc[d.id] = d.data().paymode;
                return acc;
            }, {});

            let bookingDataLookup = {};
            if (bookingIdsToFetch.length > 0) {
                const chunks = chunkArray(bookingIdsToFetch, 30);
                for (const chunk of chunks) {
                    const snap = await getDocs(
                        query(collection(db, "reservations"), where('__name__', 'in', chunk))
                    );
                    snap.docs.forEach(d => {
                        bookingDataLookup[d.id] = d.data();
                    });
                }
            }

            // 4. Format Pay History
            const payHistoryData = payHistoryDocs.map((doc1) => {
                const docData = doc1.data();
                const paymode1 = paymentModeLookup[docData.paymode1] || docData.paymode1 || "Cash";
                const paymode2 = paymentModeLookup[docData.paymode2] || docData.paymode2 || "";
                const date = docData.createdAt?.toDate ? docData.createdAt.toDate() : new Date();
                const formattedDate = moment(date).format('DD-MM-YYYY HH:mm:ss');

                let bData = {};
                const bookingDocData = bookingDataLookup[docData.bookingid];
                if (bookingDocData) {
                    bData = {
                        rooms: bookingDocData.rooms || [],
                        checkinDate: bookingDocData.checkindate || null,
                        checkoutDate: bookingDocData.checkoutdate || null,
                        custName: bookingDocData.customers?.custname || "Guest"
                    };
                }

                return {
                    id: doc1.id,
                    data: docData,
                    paymode1,
                    paymode2,
                    createdat: formattedDate,
                    bookingData: bData
                };
            });
            setpayhistory(payHistoryData);

            // 5. Payment Mode Totals
            const paymentModeTotals = allPaymentModesSnapshot.docs.map(modeDoc => {
                let total1 = 0;
                let total2 = 0;
                const paymodeId = modeDoc.id;

                payHistoryData.forEach(pay => {
                    if (pay.data.paymode1 === paymodeId) {
                        total1 += Number(pay.data.amount1 || 0);
                    }
                    if (pay.data.paymode2 === paymodeId) {
                        total2 += Number(pay.data.amount2 || 0);
                    }
                });

                return {
                    id: paymodeId,
                    paymode: modeDoc.data().paymode || "",
                    totalamount: (total1 + total2).toFixed(0)
                };
            });
            setpaymodes(paymentModeTotals);

            // 6. Checkin Details
            const [checkinBookedSnap, checkinCheckoutSnap] = await Promise.all([
                getDocs(
                    query(
                        collection(db, "checkindetails"),
                        where("status", "==", "Booked"),
                        where("uid", "==", uidToUse),
                        where("checkindate", "==", reportDate),
                        orderBy("createdAt", "asc")
                    )
                ),
                getDocs(
                    query(
                        collection(db, "checkindetails"),
                        where("status", "==", "Checkout"),
                        where("uid", "==", uidToUse),
                        where("checkindate", "==", reportDate),
                        orderBy("createdAt", "asc")
                    )
                )
            ]);

            const checkinDetailsData = [
                ...checkinCheckoutSnap.docs.map(d => ({ id: d.id, ...d.data() })),
                ...checkinBookedSnap.docs.map(d => ({ id: d.id, ...d.data() }))
            ];

            const uniqueCheckinDetails = Array.from(
                new Map(checkinDetailsData.map(item => [item.bookingid, item])).values()
            );
            const reportBookingIds = uniqueCheckinDetails.map(d => d.bookingid).filter(Boolean);

            if (reportBookingIds.length === 0) {
                setbookingData([]);
                setallllroomamount(0);
                setallllserviuceamount(0);
                setallllfoodamount(0);
                setalllltotalamount(0);
                setroomscount(0);
                setalladults(0);
                setallchilds(0);
                return;
            }

            // 7. Bulk Fetch Reservations & Orders
            const reservationsMap = {};
            const foodTotalsMap = {};

            const bookingChunks = chunkArray(reportBookingIds, 30);
            for (const chunk of bookingChunks) {
                const [resSnap, ordSnap] = await Promise.all([
                    getDocs(query(collection(db, 'reservations'), where('__name__', 'in', chunk))),
                    getDocs(
                        query(
                            collection(db, 'orders'),
                            where('status', '==', 'Bill Pending'),
                            where('orderdate', '==', reportDate),
                            where('bookingId', 'in', chunk)
                        )
                    )
                ]);

                resSnap.docs.forEach(d => {
                    reservationsMap[d.id] = { id: d.id, ...d.data() };
                });

                ordSnap.docs.forEach(d => {
                    const oData = d.data();
                    foodTotalsMap[oData.bookingId] = (foodTotalsMap[oData.bookingId] || 0) + Number(oData.totalamount || 0);
                });
            }

            const paymentHistoryMap = payHistoryData.reduce((acc, pay) => {
                const bId = pay.data.bookingid;
                if (reservationsMap[bId]) {
                    if (!acc[bId]) acc[bId] = [];
                    acc[bId].push(pay);
                }
                return acc;
            }, {});

            // 8. Totals Calculation
            let sumRoom = 0;
            let sumFood = 0;
            let sumTotal = 0;
            let sumService = 0;
            let sumRoomsCount = 0;
            let sumAdults = 0;
            let sumChilds = 0;

            const combinedData = uniqueCheckinDetails.map((checkinDetail) => {
                const booking = reservationsMap[checkinDetail.bookingid];
                if (!booking) return null;

                const roomamount = Number(booking.rooamount || 0);
                const noofdays = Number(booking.noofdays || 1);
                const serviceamount = Number(booking.serviceamount || 0) / noofdays;
                const foodTotal = foodTotalsMap[checkinDetail.bookingid] || 0;
                const totalamount = roomamount + serviceamount + foodTotal;

                const paymentHistory = paymentHistoryMap[checkinDetail.bookingid] || [];
                const paidAmount = paymentHistory.reduce(
                    (sum, p) => sum + Number(p.data.amount1 || 0) + Number(p.data.amount2 || 0),
                    0
                );

                sumRoom += roomamount;
                sumFood += foodTotal;
                sumTotal += totalamount;
                sumService += serviceamount;
                sumRoomsCount += (booking.rooms?.length || 1);
                sumAdults += Number(booking.adults || 0);
                sumChilds += Number(booking.childs || 0);

                return {
                    ...checkinDetail,
                    booking,
                    serviceamount: serviceamount.toFixed(0),
                    roomamount: roomamount.toFixed(0),
                    foodamount: foodTotal.toFixed(0),
                    totalamount: totalamount.toFixed(0),
                    paidamount: paidAmount.toFixed(0),
                    paymentHistory
                };
            }).filter(Boolean);

            setallllroomamount(sumRoom.toFixed(0));
            setallllfoodamount(sumFood.toFixed(0));
            setalllltotalamount(sumTotal.toFixed(0));
            setallllserviuceamount(sumService.toFixed(0));
            setroomscount(sumRoomsCount);
            setalladults(sumAdults);
            setallchilds(sumChilds);
            setbookingData(combinedData);

        } catch (error) {
            console.error("Error in fetchUsers:", error);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // UI Dark Theme Cell Styles
    const cellStyle = {
        color: COLORS.vanilla,
        borderColor: COLORS.borderGreen,
        fontSize: "12px",
        padding: "8px 10px"
    };

    const headerCellStyle = {
        backgroundColor: COLORS.calPolyGreen,
        color: COLORS.gold,
        fontWeight: "bold",
        fontSize: "12px",
        borderColor: COLORS.darkGreen,
        padding: "9px 10px",
        textTransform: "uppercase"
    };

    return (
        <div style={{ background: COLORS.bgDark, minHeight: "100vh", padding: "16px 8px" }}>
            {/* Integrated Print CSS */}
            <style>
                {`
                @media print {
                    /* Reset body & hide everything outside the print wrapper */
                    html, body {
                        background: #ffffff !important;
                        color: #000000 !important;
                        height: auto !important;
                        overflow: visible !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }

                    /* Hide screen buttons and non-printable areas */
                    .no-print {
                        display: none !important;
                    }

                    /* Force the main invoice wrapper to span full width */
                    .invoice-print-wrapper {
                        width: 100% !important;
                        max-width: 100% !important;
                        flex-basis: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #ffffff !important;
                        color: #000000 !important;
                        box-shadow: none !important;
                        border: none !important;
                    }

                    /* Ensure specific borders are visible for structure */
                    .print-area {
                        border: none !important;
                        background: #ffffff !important;
                        color: #000000 !important;
                    }

                    /* Fix table printing */
                    table {
                        width: 100% !important;
                        border-collapse: collapse !important;
                    }

                    th, td {
                        border: 1px solid #ddd !important;
                        padding: 8px !important;
                        color: #000000 !important;
                    }

                    th {
                        background-color: #f5f5f5 !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        font-weight: bold !important;
                    }

                    /* Clean text colors on paper */
                    .hotel-title, .print-text, p, span, h5, h6 {
                        color: #000000 !important;
                    }

                    .MuiPaper-root {
                        background-color: transparent !important;
                        box-shadow: none !important;
                        border: none !important;
                    }
                }
                `}
            </style>

            <div className="container-fluid">
                <div className="row justify-content-center">
                    {/* Invoice Print Wrapper */}
                    <div className="col-12 col-xl-11 invoice-print-wrapper print-area">
                        
                        <div
                            ref={printRef}
                            style={{
                                background: COLORS.darkGreen,
                                borderRadius: "16px",
                                border: `1px solid ${COLORS.calPolyGreen}`,
                                padding: "24px",
                                boxShadow: "0 6px 18px rgba(0,0,0,0.3)"
                            }}
                            className="print-area"
                        >
                            {/* --- HOTEL BANNER --- */}
                            <Box textAlign="center" mb={2}>
                                <Typography
                                    variant="h5"
                                    fontWeight="800"
                                    className="hotel-title"
                                    style={{
                                        color: COLORS.gold,
                                        letterSpacing: "1px",
                                        textTransform: "uppercase"
                                    }}
                                >
                                    {propData.hotelname || "Hotel Management System"}
                                </Typography>
                                <Typography variant="subtitle2" className="print-text" style={{ color: COLORS.vanilla, fontWeight: "600", marginTop: "4px" }}>
                                    GSTIN No : {propData.gstno || propData.hotelname || "N/A"}
                                </Typography>
                                <Typography variant="body2" className="print-text" style={{ color: TEXT_SECONDARY, marginTop: "4px", lineHeight: 1.5 }}>
                                    {[propData.address, propData.city, propData.state, propData.pincode].filter(Boolean).join(", ")}
                                    <br />
                                    Phone: +91 {propData.mobile || "N/A"} | Email: {propData.email || "N/A"}
                                </Typography>
                            </Box>

                            {/* --- REPORT META STRIP --- */}
                            <TableContainer
                                component={Paper}
                                sx={{
                                    mb: 3,
                                    background: COLORS.bgDark,
                                    border: `1px solid ${COLORS.calPolyGreen}`,
                                    borderRadius: "10px"
                                }}
                            >
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell align="left" width="33%" sx={cellStyle}>
                                                <Typography variant="body2" className="print-text" style={{ color: COLORS.vanilla }}>
                                                    <strong>Date:</strong> {moment().format("DD-MM-YYYY hh:mm:ss A")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center" width="34%" sx={cellStyle}>
                                                <Typography variant="subtitle1" fontWeight="800" className="print-text" style={{ color: COLORS.gold, textTransform: "uppercase" }}>
                                                    Reception Daily Shift Report
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" width="33%" sx={cellStyle}>
                                                <Typography variant="body2" className="print-text" style={{ color: COLORS.vanilla }}>
                                                    <strong>Receptionist:</strong> {username}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* --- SECTION 1: BOOKING REPORT --- */}
                            <Typography variant="subtitle2" fontWeight="700" className="print-text" style={{ color: COLORS.gold, marginBottom: "8px", textTransform: "uppercase" }}>
                                📋 Booking & Occupancy Report
                            </Typography>
                            <TableContainer
                                component={Paper}
                                sx={{
                                    mb: 3,
                                    background: COLORS.bgDark,
                                    border: `1px solid ${COLORS.calPolyGreen}`,
                                    borderRadius: "10px"
                                }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headerCellStyle}>Sr</TableCell>
                                            <TableCell sx={headerCellStyle}>Customer Name</TableCell>
                                            <TableCell sx={headerCellStyle}>Check-In</TableCell>
                                            <TableCell sx={headerCellStyle}>Check-Out</TableCell>
                                            <TableCell sx={headerCellStyle}>Rooms</TableCell>
                                            <TableCell sx={headerCellStyle} align="right">Persons</TableCell>
                                            <TableCell sx={headerCellStyle} align="right">Room (₹)</TableCell>
                                            <TableCell sx={headerCellStyle} align="right">Extra (₹)</TableCell>
                                            <TableCell sx={headerCellStyle} align="right">Total (₹)</TableCell>
                                            <TableCell sx={headerCellStyle} align="center">Payments</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {bookingData.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center" sx={{ ...cellStyle, py: 3, color: TEXT_SECONDARY }}>
                                                    No bookings registered for today's check-in date.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            bookingData.map((item, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell sx={cellStyle}>{index + 1}</TableCell>
                                                    <TableCell sx={{ ...cellStyle, textTransform: "capitalize", fontWeight: "600" }}>
                                                        {item.booking?.customers?.custname || "Guest"}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>
                                                        {moment(item.checkindate).format("DD/MM/YYYY")} {item.checkintime || ""}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>
                                                        {moment(item.checkoutdate).format("DD/MM/YYYY")} {item.checkouttime || ""}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>
                                                        {item.booking?.rooms?.map((r, i) => (
                                                            <span key={i} style={{ display: "block" }}>
                                                                Room {r.roomno || r.roomname}
                                                            </span>
                                                        ))}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle} align="right">
                                                        {item.booking?.adults || 0}A / {item.booking?.childs || 0}C
                                                    </TableCell>
                                                    <TableCell sx={cellStyle} align="right">₹{item.roomamount}</TableCell>
                                                    <TableCell sx={cellStyle} align="right">₹{item.serviceamount}</TableCell>
                                                    <TableCell sx={{ ...cellStyle, fontWeight: "700", color: COLORS.gold }} align="right">
                                                        ₹{item.totalamount}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle} align="center">
                                                        {item.paymentHistory.length > 0 ? (
                                                            item.paymentHistory.map((p, i) => (
                                                                <div key={i} style={{ fontSize: "11px" }}>
                                                                    {Number(p.data.amount1) > 0 && (
                                                                        <span style={{ color: COLORS.goldenrod, display: "block" }}>
                                                                            {p.paymode1}: ₹{p.data.amount1}
                                                                        </span>
                                                                    )}
                                                                    {Number(p.data.amount2) > 0 && (
                                                                        <span style={{ color: COLORS.vanilla, display: "block" }}>
                                                                            {p.paymode2}: ₹{p.data.amount2}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <span style={{ color: "#ff6b6b", fontWeight: "bold" }}>Pending</span>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}

                                        {/* TOTAL ROW */}
                                        <TableRow style={{ background: COLORS.calPolyGreen }}>
                                            <TableCell colSpan={4} align="right" sx={{ ...cellStyle, fontWeight: "bold", color: COLORS.gold }}>
                                                TOTAL
                                            </TableCell>
                                            <TableCell sx={{ ...cellStyle, fontWeight: "bold", color: COLORS.gold }}>
                                                {roomscount} Rooms
                                            </TableCell>
                                            <TableCell align="right" sx={{ ...cellStyle, fontWeight: "bold", color: COLORS.gold }}>
                                                {alladults}A / {allchilds}C
                                            </TableCell>
                                            <TableCell align="right" sx={{ ...cellStyle, fontWeight: "bold", color: COLORS.gold }}>
                                                ₹{allllroomamount}
                                            </TableCell>
                                            <TableCell align="right" sx={{ ...cellStyle, fontWeight: "bold", color: COLORS.gold }}>
                                                ₹{allllserviuceamount}
                                            </TableCell>
                                            <TableCell align="right" sx={{ ...cellStyle, fontWeight: "800", color: COLORS.gold }}>
                                                ₹{alllltotalamount}
                                            </TableCell>
                                            <TableCell sx={cellStyle} />
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* --- SECTION 2: INDIVIDUAL COLLECTION LOGS --- */}
                            <Typography variant="subtitle2" fontWeight="700" className="print-text" style={{ color: COLORS.gold, marginBottom: "8px", textTransform: "uppercase" }}>
                                💵 Detailed Payment Collections
                            </Typography>
                            <TableContainer
                                component={Paper}
                                sx={{
                                    mb: 3,
                                    background: COLORS.bgDark,
                                    border: `1px solid ${COLORS.calPolyGreen}`,
                                    borderRadius: "10px"
                                }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headerCellStyle}>Guest</TableCell>
                                            <TableCell sx={headerCellStyle}>Room</TableCell>
                                            <TableCell sx={headerCellStyle}>Check-In</TableCell>
                                            <TableCell sx={headerCellStyle}>Check-Out</TableCell>
                                            <TableCell sx={headerCellStyle}>Mode 1</TableCell>
                                            <TableCell sx={headerCellStyle} align="right">Amt 1 (₹)</TableCell>
                                            <TableCell sx={headerCellStyle}>Mode 2</TableCell>
                                            <TableCell sx={headerCellStyle} align="right">Amt 2 (₹)</TableCell>
                                            <TableCell sx={headerCellStyle}>Timestamp</TableCell>
                                            <TableCell sx={headerCellStyle}>Note</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {payhistory.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center" sx={{ ...cellStyle, py: 3, color: TEXT_SECONDARY }}>
                                                    No collections recorded during this shift.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            payhistory.map((item, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell sx={{ ...cellStyle, fontWeight: "600" }}>
                                                        {item.bookingData?.custName || "Guest"}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>
                                                        {item.bookingData?.rooms?.map((r, i) => (
                                                            <span key={i}>{r.roomno || r.roomname} </span>
                                                        )) || "-"}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>
                                                        {item.bookingData?.checkinDate ? moment(item.bookingData.checkinDate).format("DD-MM-YYYY") : "-"}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>
                                                        {item.bookingData?.checkoutDate ? moment(item.bookingData.checkoutDate).format("DD-MM-YYYY") : "-"}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>{item.paymode1 || "-"}</TableCell>
                                                    <TableCell sx={cellStyle} align="right">
                                                        {Number(item.data?.amount1) > 0 ? `₹${item.data.amount1}` : "-"}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>{item.paymode2 || "-"}</TableCell>
                                                    <TableCell sx={cellStyle} align="right">
                                                        {Number(item.data?.amount2) > 0 ? `₹${item.data.amount2}` : "-"}
                                                    </TableCell>
                                                    <TableCell sx={cellStyle}>{item.createdat}</TableCell>
                                                    <TableCell sx={cellStyle}>{item.data?.note || "-"}</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* --- SECTION 3: MODE-WISE SETTLEMENT SUMMARY --- */}
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" fontWeight="700" className="print-text" style={{ color: COLORS.gold, marginBottom: "8px", textTransform: "uppercase" }}>
                                        💳 Paymode-Wise Summary
                                    </Typography>
                                    <TableContainer
                                        component={Paper}
                                        sx={{
                                            background: COLORS.bgDark,
                                            border: `1px solid ${COLORS.calPolyGreen}`,
                                            borderRadius: "10px"
                                        }}
                                    >
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={headerCellStyle}>Payment Mode</TableCell>
                                                    <TableCell sx={headerCellStyle} align="right">Total Collected</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {paymodes.filter(p => Number(p.totalamount) > 0).length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={2} align="center" sx={{ ...cellStyle, color: TEXT_SECONDARY }}>
                                                            No collections by mode.
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    paymodes
                                                        .filter(p => Number(p.totalamount) > 0)
                                                        .map((item, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell sx={{ ...cellStyle, fontWeight: "600" }}>{item.paymode}</TableCell>
                                                                <TableCell sx={{ ...cellStyle, fontWeight: "700", color: COLORS.gold }} align="right">
                                                                    ₹{item.totalamount}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                )}
                                                <TableRow style={{ background: COLORS.calPolyGreen }}>
                                                    <TableCell sx={{ ...cellStyle, fontWeight: "bold", color: COLORS.vanilla }}>
                                                        SHIFT TOTAL
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ ...cellStyle, fontWeight: "800", color: COLORS.gold, fontSize: "13px" }}>
                                                        ₹{paymodes.reduce((sum, p) => sum + Number(p.totalamount || 0), 0)}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            </Grid>

                        </div>

                        {/* Print Button (Hidden on Print) */}
                        <Box textAlign="center" mt={3} className="no-print">
                            <Button
                                variant="contained"
                                onClick={handlePrint}
                                sx={{
                                    background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})`,
                                    color: COLORS.darkGreen,
                                    fontWeight: "800",
                                    padding: "8px 30px",
                                    borderRadius: "10px",
                                    "&:hover": {
                                        background: COLORS.gold
                                    }
                                }}
                            >
                                🖨️ Print Shift Report
                            </Button>
                        </Box>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default Selfreport;